import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/** A list item as shown: `enter` when it arrived after the list first showed, `out` while it fades after leaving. */
export interface Presence<T> {
  key: string
  item: T
  state: 'here' | 'enter' | 'out'
}

interface Leaving<T> {
  item: T
  /** Where it stood when it left, so it fades in its place rather than at the end: after this key, else at this index. */
  after: string | null
  index: number
  at: number
}

/**
 * A list with its arrivals and departures, for CSS transitions: an item that arrives after the first render is `enter`
 * (the first ones are just `here`, so a page load doesn't animate everyone in), and one that leaves stays `exitMs` as
 * `out` where it stood. One that comes back while fading is `here` again.
 */
export function usePresence<T>(items: readonly T[], keyOf: (item: T) => string, exitMs: number): Presence<T>[] {
  const [leaving, setLeaving] = useState<Map<string, Leaving<T>>>(() => new Map())
  const previous = useRef<readonly T[] | null>(null)
  /** Keys shown since the list first showed: anything else is arriving (read during render, so it animates at once). */
  const seen = useRef<Set<string> | null>(null)
  const entered = useRef(new Set<string>())
  if (seen.current === null) seen.current = new Set(items.map(keyOf))
  const known = seen.current

  useLayoutEffect(() => {
    const before = previous.current
    previous.current = items
    if (before === null) return
    const now = new Set(items.map(keyOf))
    items.forEach((item) => {
      const key = keyOf(item)
      if (!known.has(key)) {
        known.add(key)
        entered.current.add(key)
      }
    })
    const gone = before.flatMap((item, index) => (now.has(keyOf(item)) ? [] : [{ item, index, after: index > 0 ? keyOf(before[index - 1]) : null }]))
    gone.forEach(({ item }) => {
      // Gone: if they come back, they arrive again.
      entered.current.delete(keyOf(item))
      known.delete(keyOf(item))
    })
    setLeaving((current) => {
      const back = [...current.keys()].filter((key) => now.has(key))
      if (gone.length === 0 && back.length === 0) return current
      const next = new Map(current)
      back.forEach((key) => next.delete(key))
      const at = Date.now()
      gone.forEach(({ item, index, after }) => {
        next.set(keyOf(item), { item, index, after, at })
      })
      return next
    })
    // keyOf is expected to be stable in meaning; a new function each render mustn't rerun this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  useEffect(() => {
    if (leaving.size === 0) return
    const oldest = Math.min(...[...leaving.values()].map((entry) => entry.at))
    const timer = setTimeout(() => {
      const cutoff = Date.now() - exitMs
      setLeaving((current) => {
        const next = new Map([...current].filter(([, entry]) => entry.at > cutoff))
        return next.size === current.size ? current : next
      })
    }, Math.max(0, oldest + exitMs - Date.now()))
    return () => clearTimeout(timer)
  }, [leaving, exitMs])

  const shown: Presence<T>[] = items.map((item) => {
    const key = keyOf(item)
    return { key, item, state: entered.current.has(key) || !known.has(key) ? 'enter' : 'here' }
  })
  const outgoing = [...leaving.entries()].sort((a, b) => a[1].index - b[1].index)
  for (const [key, { item, index, after }] of outgoing) {
    if (shown.some((entry) => entry.key === key)) continue
    const neighbour = after === null ? -1 : shown.findIndex((entry) => entry.key === after)
    const at = after === null ? 0 : neighbour >= 0 ? neighbour + 1 : Math.min(index, shown.length)
    shown.splice(at, 0, { key, item, state: 'out' })
  }
  return shown
}
